import { useState, type ReactNode } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Session } from "../api/models";
import { PButton } from "../ui/PButton";
import { PSelectButton } from "../ui/PSelectButton";
import { PSidebar } from "../ui/PSidebar";
import { PrimeGlyph } from "../ui/PrimeGlyph";
import { useToast } from "../ui/toast";
import { useInPlayGreet, useInPlayNote, usePlayerCounts } from "./api";
import { SessionMoney } from "./SessionCard";

// `pi pi-search-minus` (PRIMEICONS 7, MIT), WHICH `prime-glyphs.ts` DOES NOT CARRY
function SearchMinus() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" style={{ flexShrink: 0, display: "block" }}>
      <path d="M10.77,18.3a7.53,7.53,0,1,1,7.53-7.53A7.54,7.54,0,0,1,10.77,18.3Zm0-13.55a6,6,0,1,0,6,6A6,6,0,0,0,10.77,4.75Z" />
      <path d="M20,20.75a.74.74,0,0,1-.53-.22L15.34,16.4a.75.75,0,0,1,1.06-1.06l4.13,4.13a.75.75,0,0,1,0,1.06A.74.74,0,0,1,20,20.75Z" />
      <path d="M13.25,11.5h-5a.75.75,0,0,1,0-1.5h5a.75.75,0,0,1,0,1.5Z" />
    </svg>
  );
}

// `.bg-selected-location` (`_pillars.scss`): ORANGE 600 FILL, `--surface-0` TEXT, WEIGHT 600.
// HOVER STILL TAKES THE BUTTON'S OWN HOVER FILL, AS THE THEME'S MORE SPECIFIC RULE DOES IN ANGULAR.
export const SELECTED_LOCATION_SX = { backgroundColor: "var(--orange-600)", borderColor: "var(--orange-600)", color: "var(--surface-0)", fontWeight: 600 };

// THE MAP-MARKER HEADLINE BOTH SIDEBARS OPEN WITH: A 1.5REM `pi-map-marker` AND THE LOCATION
function LocationHeadline({ location, colorClass }: { location: string | undefined; colorClass: string }) {
  return (
    <div className={`flex flex-row align-items-center font-bold text-lg ${colorClass}`}>
      <PrimeGlyph name="map-marker" size="1.5rem" />
      <div className="ml-2">{location}</div>
    </div>
  );
}

// PORT OF THE "SELECTED SESSION" `p-sidebar`: NON-MODAL, LEFT, `w-25rem`, AND `hidden md:block` -
// ON A PHONE IT NEVER SHOWS. THE ORANGE LOCATION WITH A ZOOM IN / ZOOM OUT TOGGLE, THE SESSION
// CARD (CARD ICON, NAME, SITE, MACHINE, DURATION / ACTUAL / THEO), AND A FULL-WIDTH CANCEL.
export function SelectedSessionSidebar(props: {
  open: boolean;
  session: Session | null;
  zoomedIn: boolean;
  onClose: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCancel: () => void;
}) {
  const { open, session: s, zoomedIn, onClose, onZoomIn, onZoomOut, onCancel } = props;
  const md = useMediaQuery("(min-width: 768px)");
  return (
    <PSidebar open={open && md && s !== null} onClose={onClose} position="left" modal={false} width="25rem" ariaLabel="Selected session">
      <div className="flex flex-row align-items-center justify-content-between">
        <LocationHeadline location={s?.location} colorClass="color-orange-600" />
        <div>
          {zoomedIn ? (
            <PButton label="Zoom Out" icon={<SearchMinus />} hostElement className="font-bold min-width-125" sx={{ fontWeight: 700 }} onClick={onZoomOut} />
          ) : (
            <PButton label="Zoom In" icon="search" hostElement className="font-bold min-width-125" sx={{ fontWeight: 700 }} onClick={onZoomIn} />
          )}
        </div>
      </div>
      <div className="mt-4 p-4 border-1 surface-border surface-card border-round flex flex-column">
        <div>
          <div className="flex flex-row align-items-center">
            {s?.cmsPlayerId ? (
              <span className="material-symbols-rounded color-primary">credit_card</span>
            ) : (
              <span className="material-symbols-rounded">credit_card_off</span>
            )}
            <div className="text-overflow-ellipsis font-bold ml-3">{(s?.playerName ?? "").toUpperCase()}</div>
          </div>
          {s?.siteName && (
            <div className="mt-1">
              <span className="text-sm font-bold">{s.siteName}</span>
            </div>
          )}
          <div className="mt-2 w-full flex flex-row align-items-center justify-content-between gap-2">
            <div className="text-overflow-ellipsis text-sm">{s?.description}</div>
          </div>
          <div className="mt-3 flex flex-row align-items-center justify-content-between">{s && <SessionMoney session={s} carded={!!s.cmsPlayerId} />}</div>
        </div>
      </div>
      <div className="mt-3">
        <PButton label="Cancel" severity="warning" hostElement className="w-full bg-selected-location" sx={{ ...SELECTED_LOCATION_SX, width: "100%" }} onClick={onCancel} />
      </div>
    </PSidebar>
  );
}

// A `p-accordion` OF SEVERAL TABS, `[activeIndex]="0"`: THE FIRST TAB STARTS OPEN AND OPENING ONE
// CLOSES THE OTHER. THE THEME SQUARES THE INNER CORNERS AND DROPS THE DOUBLED TOP BORDER.
function Accordion({ tabs, className = "" }: { tabs: Array<{ header: ReactNode; body: ReactNode }>; className?: string }) {
  const [active, setActive] = useState<number | null>(0);
  const last = tabs.length - 1;
  return (
    <div className={className}>
      {tabs.map((t, i) => {
        const open = active === i;
        return (
          <div key={i}>
            <button
              type="button"
              className="pillars-accordion-header"
              aria-expanded={open}
              onClick={() => setActive(open ? null : i)}
              style={{
                borderRadius: `${i === 0 ? "4px 4px" : "0 0"} ${i === last && !open ? "4px 4px" : "0 0"}`,
                borderTop: i === 0 ? undefined : "0 none",
              }}
            >
              <span style={{ display: "flex", marginRight: "0.5rem" }}>
                <PrimeGlyph name={open ? "chevron-down" : "chevron-right"} />
              </span>
              {t.header}
            </button>
            {open && (
              <div className="pillars-accordion-content" style={{ borderRadius: i === last ? "0 0 4px 4px" : 0 }}>
                {t.body}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CountHeader({ label, count }: { label: string; count: string }) {
  return (
    <span className="flex align-items-center gap-2 w-full">
      <span className="font-bold white-space-nowrap"> {label} </span>
      <span className="pillars-badge ml-auto mr-2">{count}</span>
    </span>
  );
}

// PORT OF THE "ACTIONS" `p-sidebar` OPENED FROM A CARD'S KEBAB: MODAL, RIGHT, `w-full md:w-30rem`.
// THE LOCATION AND SESSION CARD, THEN A "GREET" TAB (COMPLETED / NOT FOUND, A 150-CHARACTER
// MESSAGE, ADD) AND A "NOTE" TAB (A 150-CHARACTER NOTE, ADD ONCE IT HAS TEXT), EACH HEADER
// COUNTING THE PLAYER'S GREETS / NOTES. A SAVE CLOSES THE SIDEBAR. THE PAGE MOUNTS THIS PER
// SESSION (`key`), SO WHAT WAS TYPED FOR ONE PLAYER NEVER CARRIES OVER TO ANOTHER.
export function ActionsSidebar(props: { session: Session | null; onClose: () => void }) {
  const { session: s, onClose } = props;
  const toast = useToast();
  const counts = usePlayerCounts(s?.playerId);
  const greet = useInPlayGreet();
  const note = useInPlayNote();
  const [status, setStatus] = useState<"COMPLETED" | "NOT-FOUND">("COMPLETED");
  const [greetMessage, setGreetMessage] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const busy = greet.isPending || note.isPending;

  const addGreet = () => {
    if (!s) return;
    greet.mutate(
      { cmsPlayerId: s.cmsPlayerId, location: s.location, message: greetMessage, status },
      {
        onSuccess: () => {
          toast("success", "Add Greet", "Your greet was successfully saved.");
          onClose();
        },
        onError: () => toast("error", "Add Greet", "Your greet not was successfully saved. Please try again"),
      },
    );
  };
  const addNote = () => {
    if (!s || !noteMessage) return;
    note.mutate(
      { cmsPlayerId: s.cmsPlayerId, message: noteMessage },
      {
        onSuccess: () => {
          toast("success", "Add Note", "Your note was successfully saved.");
          onClose();
        },
        onError: () => toast("error", "Add Note", "Your note not was successfully saved. Please try again"),
      },
    );
  };

  return (
    <PSidebar open={s !== null} onClose={onClose} position="right" width={{ xs: "100%", md: "30rem" }} ariaLabel="Session actions">
      <div className="flex flex-row align-items-center justify-content-between">
        <LocationHeadline location={s?.location} colorClass="color-primary" />
      </div>
      <div className="mt-4 p-4 border-1 surface-border surface-card border-round flex flex-column">
        <div>
          <div className="flex flex-row align-items-center">
            {s?.cmsPlayerId && <span className="material-symbols-rounded color-primary">credit_card</span>}
            <div className="text-overflow-ellipsis font-bold ml-3">{(s?.playerName ?? "").toUpperCase()}</div>
          </div>
          <div className="mt-2 w-full flex flex-row align-items-center justify-content-between gap-2">
            <div className="text-overflow-ellipsis text-sm">{s?.description}</div>
          </div>
        </div>
      </div>
      <Accordion
        className="mt-3"
        tabs={[
          {
            header: <CountHeader label="Greet" count={(counts.data?.greetCount ?? 0).toFixed()} />,
            body: (
              <>
                <div>
                  <PSelectButton
                    ariaLabel="Greet status"
                    value={status}
                    onChange={setStatus}
                    options={[
                      { label: "Completed", value: "COMPLETED" },
                      { label: "Not Found", value: "NOT-FOUND" },
                    ]}
                  />
                </div>
                <div className="mt-4 flex flex-column">
                  <label className="font-bold" htmlFor="in-play-greet-message">
                    Message:
                  </label>
                  <textarea
                    id="in-play-greet-message"
                    className="p-inputtext w-full mt-1"
                    style={{ resize: "none" }}
                    rows={5}
                    maxLength={150}
                    value={greetMessage}
                    onChange={(e) => setGreetMessage(e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <PButton label="Add" hostElement className="w-full" sx={{ width: "100%" }} loading={greet.isPending} disabled={busy} onClick={addGreet} />
                </div>
              </>
            ),
          },
          {
            header: <CountHeader label="Note" count={(counts.data?.noteCount ?? 0).toFixed()} />,
            body: (
              <>
                <div className="mt-4 flex flex-column">
                  <label className="font-bold" htmlFor="in-play-note-message">
                    Note:
                  </label>
                  <textarea
                    id="in-play-note-message"
                    className="p-inputtext w-full mt-1"
                    style={{ resize: "none" }}
                    rows={5}
                    maxLength={150}
                    value={noteMessage}
                    onChange={(e) => setNoteMessage(e.target.value)}
                  />
                </div>
                <div className="mt-4">
                  <PButton label="Add" hostElement className="w-full" sx={{ width: "100%" }} loading={note.isPending} disabled={!noteMessage || busy} onClick={addNote} />
                </div>
              </>
            ),
          },
        ]}
      />
    </PSidebar>
  );
}
